import type { QueryClient } from '@tanstack/react-query';

import type { AppDispatch } from 'state/store';
import {
  ActiveDefectLayerId,
  buildActiveLayerId,
  createActiveLayerInstanceId,
  layerAdded,
  layerMapRenderPending,
  layerMapRenderSettled,
  layerRemoved,
  layerVisualizationSettingsSaved,
} from 'state/surveyDataManager';
import { getDefectFeatures } from 'domain/surveyDataManager/api/features';
import { getDefectRules } from 'domain/surveyDataManager/api/rulesets';
import {
  isMockScannerDefectSlug,
  isScannerMockCatalogueEnabled,
} from 'domain/surveyDataManager/api/scannerMockResponses';
import { getActiveLayerVisualizationMeasureSettings } from 'domain/surveyDataManager/api/visualizationMeasureSettings';
import {
  DefectLayerKind,
  DefectMeasurementSlug,
  DefectRulesetSlug,
  DefectSlug,
  DefectSurveyYearSlug,
} from 'domain/surveyDataManager/contracts';
import { surveyDataManagerQueryKeys } from 'domain/surveyDataManager/hooks';
import { isDefectOverlayLayerKind } from 'domain/surveyDataManager/defectOverlays/overlayKinds';
import type { RecipeFeatureFilter } from 'domain/surveyDataManager/visualization/recipeFeatureFilter';

export interface ActivatableMapLayer {
  readonly defectSlug: DefectSlug;
  readonly measurementSlug: DefectMeasurementSlug;
  readonly surveyYearSlug: DefectSurveyYearSlug;
  readonly rulesetSlug: DefectRulesetSlug;
  readonly lane: string;
  readonly displayName: string;
  readonly recipeId?: string | null;
  readonly featureFilter?: RecipeFeatureFilter | null;
  readonly layerKind?: DefectLayerKind;
  readonly sourceLayerId?: string | null;
  readonly vectorSourceUrl?: string | null;
}

interface PreparedLayer extends ActivatableMapLayer {
  readonly layerId: ActiveDefectLayerId;
  readonly instanceId: ReturnType<typeof createActiveLayerInstanceId>;
  readonly wasAlreadyActive: boolean;
  readonly layerKind: DefectLayerKind;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return 'Failed to activate layers.';
}

function shouldSkipRemoteVisualizationSettings(defectSlug: DefectSlug): boolean {
  return (
    isScannerMockCatalogueEnabled() && isMockScannerDefectSlug(String(defectSlug))
  );
}

function resolveLayerKind(layer: ActivatableMapLayer): DefectLayerKind {
  return layer.layerKind ?? 'map-vector';
}

/**
 * Add layers to the session store and prefetch map/visualisation data.
 * Shared by the Add Layer wizard and recipe activation.
 */
export async function activateActiveLayers(options: {
  readonly layers: readonly ActivatableMapLayer[];
  readonly dispatch: AppDispatch;
  readonly queryClient: QueryClient;
  readonly existingLayerIds?: ReadonlySet<string>;
}): Promise<void> {
  const { layers, dispatch, queryClient, existingLayerIds = new Set() } = options;
  if (layers.length === 0) {
    return;
  }

  const preparedLayers: readonly PreparedLayer[] = layers.map((layer) => {
    const layerId = buildActiveLayerId({
      defectSlug: layer.defectSlug,
      measurementSlug: layer.measurementSlug,
      surveyYearSlug: layer.surveyYearSlug,
      rulesetSlug: layer.rulesetSlug,
      lane: layer.lane,
    });
    return {
      ...layer,
      layerKind: resolveLayerKind(layer),
      layerId,
      instanceId: createActiveLayerInstanceId(),
      wasAlreadyActive: existingLayerIds.has(layerId),
    };
  });

  const newlyAdded = preparedLayers.filter((layer) => !layer.wasAlreadyActive);
  const pendingLayerIds: readonly ActiveDefectLayerId[] =
    newlyAdded.length > 0
      ? newlyAdded.map((layer) => layer.layerId)
      : preparedLayers[0]
        ? [preparedLayers[0].layerId]
        : [];
  for (const pendingLayerId of pendingLayerIds) {
    dispatch(layerMapRenderPending(pendingLayerId));
  }

  try {
    for (const layer of preparedLayers) {
      if (layer.wasAlreadyActive) {
        continue;
      }
      dispatch(
        layerAdded({
          defectSlug: layer.defectSlug,
          measurementSlug: layer.measurementSlug,
          surveyYearSlug: layer.surveyYearSlug,
          rulesetSlug: layer.rulesetSlug,
          lane: layer.lane,
          displayName: layer.displayName,
          instanceId: layer.instanceId,
          recipeId: layer.recipeId ?? null,
          featureFilter: layer.featureFilter ?? null,
          layerKind: layer.layerKind,
          sourceLayerId: layer.sourceLayerId ?? null,
          vectorSourceUrl: layer.vectorSourceUrl ?? null,
        }),
      );
    }

    await Promise.all(
      preparedLayers.flatMap((layer) => {
        if (isDefectOverlayLayerKind(layer.layerKind)) {
          return [];
        }
        return [
          queryClient.prefetchQuery({
            queryKey: surveyDataManagerQueryKeys.defectFeatures(
              layer.defectSlug,
              layer.measurementSlug,
              layer.surveyYearSlug,
              layer.lane,
            ),
            queryFn: () =>
              getDefectFeatures(
                layer.defectSlug,
                layer.measurementSlug,
                layer.surveyYearSlug,
                layer.lane || undefined,
              ),
          }),
          queryClient.prefetchQuery({
            queryKey: surveyDataManagerQueryKeys.defectRules(
              layer.defectSlug,
              layer.rulesetSlug,
              layer.measurementSlug,
            ),
            queryFn: () =>
              getDefectRules(layer.defectSlug, layer.rulesetSlug, {
                measurementSlug: layer.measurementSlug,
              }),
          }),
        ];
      }),
    );

    await Promise.all(
      preparedLayers.map(async (layer) => {
        if (
          isDefectOverlayLayerKind(layer.layerKind) ||
          shouldSkipRemoteVisualizationSettings(layer.defectSlug)
        ) {
          return;
        }
        try {
          const visualizationResponse = await queryClient.fetchQuery({
            queryKey: surveyDataManagerQueryKeys.activeLayerVisualizationSettings(
              layer.instanceId,
            ),
            queryFn: () =>
              getActiveLayerVisualizationMeasureSettings(layer.instanceId),
          });
          if (visualizationResponse?.settings) {
            dispatch(
              layerVisualizationSettingsSaved({
                id: layer.layerId,
                settings: visualizationResponse.settings,
              }),
            );
          }
        } catch {
        }
      }),
    );
  } catch (error) {
    for (const layer of newlyAdded) {
      dispatch(layerRemoved({ id: layer.layerId }));
    }
    for (const pendingLayerId of pendingLayerIds) {
      dispatch(layerMapRenderSettled(pendingLayerId));
    }
    throw new Error(getErrorMessage(error));
  }
}
